import { HeaderMediaFrame } from "./getPrimaryHeaderImageUrl";
import { css, keyframes } from "@emotion/react";
import { styleUtils } from "@minted/minted-components";
import React, { memo } from "react";

/**
 * Marquee media treatment — continuous horizontal slide of photos. Fixed
 * height only; width follows each image's intrinsic aspect ratio. Frames are
 * flush (no gaps). Design theme does not style these images; layout CSS +
 * animation only.
 *
 * The track is duplicated so translateX(-50%) loops seamlessly. Animation
 * respects prefers-reduced-motion.
 */
const marqueeSlide = keyframes`
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-50%);
  }
`;

const IMAGE_HEIGHT = styleUtils.rem(520);
/** Minimum photos in one loop half so a short gallery still fills the strip. */
const MIN_STRIP_LENGTH = 4;

const styles = {
  frame: css`
    flex-shrink: 0;
    position: relative;
  `,
  // Height is locked; width: auto keeps natural proportions (no crop).
  image: css`
    display: block;
    height: ${IMAGE_HEIGHT};
    width: auto;
  `,
  track: css`
    align-items: stretch;
    display: flex;
    flex-direction: row;
    width: max-content;

    @media (prefers-reduced-motion: no-preference) {
      animation: ${marqueeSlide} 40s linear infinite;
    }
  `,
  viewport: css`
    margin-bottom: ${styleUtils.rem(30)};
    overflow: hidden;
    width: 100%;
  `,
};

export type MarqueeMediaProps = {
  alt: string;
  frames: HeaderMediaFrame[];
};

/**
 * Repeat the gallery until the strip has enough frames for a continuous look.
 */
const expandFrames = (frames: HeaderMediaFrame[]): HeaderMediaFrame[] => {
  if (frames.length === 0) {
    return [];
  }

  const expanded = [...frames];

  while (expanded.length < MIN_STRIP_LENGTH) {
    expanded.push(...frames);
  }

  return expanded;
};

const MarqueeMedia = ({ alt, frames }: MarqueeMediaProps) => {
  const stripFrames = expandFrames(frames);
  // Duplicate so -50% translate lands on the start of the second copy.
  const loopedFrames = [...stripFrames, ...stripFrames];

  if (stripFrames.length === 0) {
    return null;
  }

  return (
    <div css={styles.viewport} data-cy="marqueeMedia">
      <div css={styles.track}>
        {loopedFrames.map((frame, index) => {
          const isPrimaryStrip = index < stripFrames.length;

          return (
            <div
              css={styles.frame}
              key={`${frame.url}-${frame.position}-${index}`}
            >
              <img
                alt={alt}
                css={styles.image}
                // Second half is decorative for the seamless loop.
                data-cy={isPrimaryStrip ? "marqueeImage" : undefined}
                data-slot-index={frame.position}
                src={frame.url}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MemoizedMarqueeMedia = memo(MarqueeMedia);

export { MemoizedMarqueeMedia as MarqueeMedia };
